import { Coins } from '@phosphor-icons/react';
import { useState, useMemo } from 'react';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import BalancesWidget from '../home/widgets/BalancesWidget';
import AccountWidget from './widgets/AccountWidget';
import CardWidget from './widgets/CardWidget';
import LoanPaymentPlansWithChartsWidget from './widgets/LoanPaymentPlansWithChartsWidget';
import DepositModal from './widgets/modals/DepositModal';
import TransferChooserModal from './widgets/modals/TransferChooserModal';
import WithdrawModal from './widgets/modals/WithdrawModal';
import {
  useDepositCustomerId,
  useLinkedAccountsQuery,
} from './hooks/useDeposits';
import StatementHistoryWidget from './widgets/StatementHistoryWidget';
import { useQuery } from '@tanstack/react-query';
import {
  BalancesResponse,
  LoanWithReason,
  LoanStatuses,
  FetchLoansOrderBy,
  FetchLoansRequestBody,
  CustomerWithDriverOnboarding,
  RevenueExpensesData,
} from '../../../../../types/apiTypes';
import { useUser } from '../../../../../utils/UserContext';
import useApi from '../../../../../utils/AxiosWrapper';
import { unwrapApiResponse } from '../../../../../utils/unwrapApiResponse';
import {
  Customer,
  InternalBankAccount,
  MonthlyStatement,
} from '../../../../../types/dbTypes';
import { useNavigate } from 'react-router-dom';
import {
  isPreActiveCustomer,
  getDemoBalances,
  getDemoAccountInfo,
  getDemoMonthlyStatements,
  getDemoLoans,
  getDemoRevenueExpensesData,
} from '../home/utils/demoData';
import { getPrev60DayAvgRevenue } from '../home/utils/revenueMetrics';
import { DemoDataAlert } from '../home/components/DemoDataAlert';

interface BankingOverviewProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function BankingOverview({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: BankingOverviewProps) {
  const { screenView } = useScreenView();
  const [withdrawPopupVisible, setWithdrawPopupVisible] = useState(false);
  const [depositPopupVisible, setDepositPopupVisible] = useState(false);
  const [transferChooserVisible, setTransferChooserVisible] = useState(false);
  const navigate = useNavigate();
  const { userId, userRecord } = useUser();
  const { callProtectedApi } = useApi();
  // Prefetch linked accounts the moment the Transfer chooser opens, so the deposit modal opens with
  // them already cached (no loading spinner). React Query dedupes if the modal's own query fires
  // first, so this is strictly ≥ the previous fetch-on-modal-open behavior.
  const depositCustomerId = useDepositCustomerId();
  useLinkedAccountsQuery(depositCustomerId, transferChooserVisible);
  const updateWithdrawPopup = (isOpen: boolean) => {
    setWithdrawPopupVisible(isOpen);
  };
  const updateDepositPopup = (isOpen: boolean) => {
    setDepositPopupVisible(isOpen);
  };

  // Check if customer is pre-active (should use demo data)
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;

  const fetchBalancesData = async () => {
    const response = await callProtectedApi(
      `/internalBankAccounts/${userId ?? ''}/balances`,
      'get',
      {},
      'Error occurred while fetching your account balances.'
    );
    return unwrapApiResponse<{ balances: BalancesResponse }>(
      response,
      'Error occurred while fetching your account balances.'
    ).balances;
  };

  const fetchMonthlyStatementsData = async () => {
    const response = await callProtectedApi(
      `/monthlyStatements/${userId ?? ''}`,
      'get',
      {}
    );
    return response?.data?.data?.monthlyStatements || [];
  };

  const fetchAccountInfo = async () => {
    const response = await callProtectedApi(
      `/internalBankAccounts/${userId ?? ''}`,
      'get',
      {},
      'Error occurred while fetching your account info.'
    );
    return unwrapApiResponse<{ internalBankAccount: InternalBankAccount }>(
      response,
      'Error occurred while fetching your account info.'
    ).internalBankAccount;
  };

  const fetchRevenueExpensesData = async () => {
    const end_date = new Date().toISOString().split('T')[0]; // Current date in YYYY-MM-DD
    const start_date = new Date(Date.now() - 150 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0]; // ~6 months ago
    const response = await callProtectedApi(
      `/charts/revenue-expenses-chart/${userId ?? ''}/all?start_date=${start_date}&end_date=${end_date}`,
      'get',
      {}
    );
    return (
      unwrapApiResponse<{ revenueExpensesData: RevenueExpensesData[] }>(
        response,
        'Error occurred while fetching your revenue and expenses.'
      ).revenueExpensesData ?? []
    );
  };

  const { data: balances, isLoading: loadingBalances } =
    useQuery<BalancesResponse>({
      queryKey: ['balancesData', userId],
      queryFn: () => fetchBalancesData(),
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const { data: monthlyStatements, isLoading: loadingMonthlyStatements } =
    useQuery<MonthlyStatement[]>({
      queryKey: ['monthlyStatementsData', userId],
      queryFn: () => fetchMonthlyStatementsData(),
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const { data: accountInfo, isLoading: loadingAccountInfo } =
    useQuery<InternalBankAccount>({
      queryKey: ['accountInfo', userId],
      queryFn: () => fetchAccountInfo(),
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const { data: revenueExpensesData } = useQuery<RevenueExpensesData[]>({
    queryKey: ['revenueExpensesData', userId],
    queryFn: () => fetchRevenueExpensesData(),
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const { data: loans = [], isLoading: loadingLoans } = useQuery<
    LoanWithReason[]
  >({
    queryKey: ['loansWithCharts', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(`/credit/loans`, 'post', {
        customer_id: userId,
        status: LoanStatuses.Active,
        order_by: FetchLoansOrderBy.ORIGINATED_AT,
        order_direction: 'DESC',
      } as FetchLoansRequestBody);
      const loans = response?.data?.data?.loans || [];
      loans.map((loan: any) => {
        loan.paid = loan?.total_cost_of_loan - loan?.current_balance;
        return loan;
      });
      return loans || [];
    },
    enabled: shouldFetchRealData,
  });

  // ============================================================================
  // Data Resolution: Use demo data for pre-active customers, real data otherwise
  // ============================================================================

  const displayBalances = useMemo(() => {
    return isPreActive
      ? getDemoBalances()
      : (balances ?? ({} as BalancesResponse));
  }, [isPreActive, balances]);

  const displayAccountInfo = useMemo(() => {
    return isPreActive
      ? getDemoAccountInfo()
      : (accountInfo ?? ({} as InternalBankAccount));
  }, [isPreActive, accountInfo]);

  const displayMonthlyStatements = useMemo(() => {
    return isPreActive ? getDemoMonthlyStatements() : (monthlyStatements ?? []);
  }, [isPreActive, monthlyStatements]);

  const displayLoans = useMemo(() => {
    return isPreActive ? getDemoLoans() : loans;
  }, [isPreActive, loans]);

  const displayRevenueExpensesData = useMemo(() => {
    return isPreActive
      ? getDemoRevenueExpensesData()
      : (revenueExpensesData ?? []);
  }, [isPreActive, revenueExpensesData]);

  const prev60DayAvgRevenue = useMemo(
    () => getPrev60DayAvgRevenue(displayRevenueExpensesData),
    [displayRevenueExpensesData]
  );

  // Loading states: Never show loading for demo data
  const displayLoadingBalances = isPreActive ? false : loadingBalances;
  const displayLoadingAccountInfo = isPreActive ? false : loadingAccountInfo;
  const displayLoadingMonthlyStatements = isPreActive
    ? false
    : loadingMonthlyStatements;
  const displayLoadingLoans = isPreActive ? false : loadingLoans;
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="flex h-full w-[1148px] max-w-[1148px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden py-[16px]">
            {isPreActive && <DemoDataAlert />}
            {withdrawPopupVisible && (
              <WithdrawModal
                onClose={() => setWithdrawPopupVisible(false)}
                isOpen={withdrawPopupVisible}
                balances={displayBalances}
                accountInfo={displayAccountInfo}
              />
            )}
            {depositPopupVisible && (
              <DepositModal
                onClose={() => setDepositPopupVisible(false)}
                isOpen={depositPopupVisible}
                internalBankAccountId={displayAccountInfo?.account_id ?? ''}
              />
            )}
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <BalancesWidget
                balances={displayBalances}
                loadingBalances={displayLoadingBalances}
                isPreActive={isPreActive}
                prev60DayAvgRevenue={prev60DayAvgRevenue}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center gap-x-[16px] align-middle">
              <AccountWidget
                onTransfer={() => setTransferChooserVisible(true)}
                accountInfo={displayAccountInfo}
                loadingAccountInfo={displayLoadingAccountInfo}
                isPreActive={isPreActive}
              />{' '}
              <CardWidget
                account_id={displayAccountInfo?.account_id ?? ''}
                isPreActive={isPreActive}
              />{' '}
              <StatementHistoryWidget
                monthlyStatements={
                  displayMonthlyStatements as MonthlyStatement[]
                }
                loadingMonthlyStatements={displayLoadingMonthlyStatements}
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <LoanPaymentPlansWithChartsWidget
                isLoading={displayLoadingLoans}
                loans={displayLoans as LoanWithReason[]}
                addClass={'w-[100%]'}
                isDemoData={isPreActive}
              />
            </div>
          </div>
        );
      case 'tablet':
        return (
          <div className="flex flex-col overflow-y-auto overflow-x-hidden">
            {withdrawPopupVisible && (
              <WithdrawModal
                onClose={() => setWithdrawPopupVisible(false)}
                isOpen={withdrawPopupVisible}
                balances={displayBalances}
                accountInfo={displayAccountInfo}
              />
            )}
            {depositPopupVisible && (
              <DepositModal
                onClose={() => setDepositPopupVisible(false)}
                isOpen={depositPopupVisible}
                internalBankAccountId={displayAccountInfo?.account_id ?? ''}
              />
            )}
            {/* <OnboardingModal /> */}
            <div className="flex h-full w-[568px] max-w-[568px] flex-col space-y-[16px] py-[16px] pb-12">
              {isPreActive && <DemoDataAlert />}
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <BalancesWidget
                  balances={displayBalances}
                  loadingBalances={displayLoadingBalances}
                  isPreActive={isPreActive}
                  prev60DayAvgRevenue={prev60DayAvgRevenue}
                />
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <CardWidget
                  account_id={displayAccountInfo?.account_id ?? ''}
                  isPreActive={isPreActive}
                />{' '}
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <AccountWidget
                  onTransfer={() => setTransferChooserVisible(true)}
                  accountInfo={displayAccountInfo}
                  loadingAccountInfo={displayLoadingAccountInfo}
                  isPreActive={isPreActive}
                />{' '}
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <StatementHistoryWidget
                  monthlyStatements={
                    displayMonthlyStatements as MonthlyStatement[]
                  }
                  loadingMonthlyStatements={displayLoadingMonthlyStatements}
                  isPreActive={isPreActive}
                />{' '}
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <LoanPaymentPlansWithChartsWidget
                  isLoading={displayLoadingLoans}
                  loans={displayLoans as LoanWithReason[]}
                  addClass={'w-[100%]'}
                  isDemoData={isPreActive}
                />
              </div>
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div className="relative flex w-full flex-col items-center overflow-x-hidden">
            {withdrawPopupVisible && (
              <WithdrawModal
                onClose={() => setWithdrawPopupVisible(false)}
                isOpen={withdrawPopupVisible}
                balances={displayBalances}
                accountInfo={displayAccountInfo}
              />
            )}
            {depositPopupVisible && (
              <DepositModal
                onClose={() => setDepositPopupVisible(false)}
                isOpen={depositPopupVisible}
                internalBankAccountId={displayAccountInfo?.account_id ?? ''}
              />
            )}
            {/* <OnboardingModal /> */}
            <div className="flex w-[343px] max-w-[343px] flex-col space-y-[16px] py-[16px] pb-[120px]">
              {isPreActive && <DemoDataAlert />}
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <BalancesWidget
                  balances={displayBalances}
                  loadingBalances={displayLoadingBalances}
                  isPreActive={isPreActive}
                  prev60DayAvgRevenue={prev60DayAvgRevenue}
                />
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <CardWidget
                  account_id={displayAccountInfo?.account_id ?? ''}
                  isPreActive={isPreActive}
                />{' '}
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <AccountWidget
                  onTransfer={() => setTransferChooserVisible(true)}
                  accountInfo={displayAccountInfo}
                  loadingAccountInfo={displayLoadingAccountInfo}
                  isPreActive={isPreActive}
                />{' '}
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <StatementHistoryWidget
                  monthlyStatements={
                    displayMonthlyStatements as MonthlyStatement[]
                  }
                  loadingMonthlyStatements={displayLoadingMonthlyStatements}
                  isPreActive={isPreActive}
                />{' '}
              </div>
              <div className="flex w-full flex-row items-center justify-center align-middle">
                <LoanPaymentPlansWithChartsWidget
                  isLoading={displayLoadingLoans}
                  loans={displayLoans as LoanWithReason[]}
                  addClass={'w-[100%]'}
                  isDemoData={isPreActive}
                />
              </div>
            </div>
            <div className="fixed bottom-0 left-0 z-40 flex h-[80px] min-h-[80px] w-full flex-col items-center justify-center border-t-[1px] border-t-bordergray bg-white">
              <AccentButtonOutline
                onClick={() => navigate('/dashboard/banking/cash-advance')}
                addClass={`px-20 py-[8px] rounded-full justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
                disabled={!(userRecord as Customer)?.is_cash_advance_active}
              >
                <div className="flex flex-row items-center space-x-2 align-middle">
                  <Coins size={20} />
                  <p className="text-[16px] text-primary">
                    Request Cash Advance
                  </p>
                </div>
              </AccentButtonOutline>
            </div>
          </div>
        );
    }
  };
  return (
    <>
      {renderScreenView(screenView)}
      {transferChooserVisible && (
        <TransferChooserModal
          isOpen={transferChooserVisible}
          onClose={() => setTransferChooserVisible(false)}
          onSelectDeposit={() => {
            setTransferChooserVisible(false);
            updateDepositPopup(true);
          }}
          onSelectWithdraw={() => {
            setTransferChooserVisible(false);
            updateWithdrawPopup(true);
          }}
        />
      )}
    </>
  );
}

export default BankingOverview;
